// =============================================================================
// Modal de confirmación tras cobrar una venta. Patrón Alegra/Defontana: ✓ verde,
// total grande, vuelto, ENVIAR POR CORREO como acción primaria, IMPRIMIR
// secundario, NUEVA VENTA primario derecha.
// =============================================================================
import { useState } from "react";
import { money } from "../lib/utils";

export interface PostSaleData {
  total: number;
  change: number;
  defaultEmail?: string;
}

export function PostSaleModal({ data, onPrint, onSendEmail, onNewSale }: {
  data: PostSaleData;
  onPrint: () => void | Promise<void>;
  onSendEmail: (email: string) => Promise<{ ok: boolean; message: string }>;
  onNewSale: () => void;
}) {
  const [email, setEmail] = useState(data.defaultEmail ?? "");
  const [sending, setSending] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [status, setStatus] = useState<{ kind: "ok" | "err"; msg: string } | null>(null);

  async function send() {
    if (!email.trim()) { setStatus({ kind: "err", msg: "Indica un correo válido." }); return; }
    setSending(true); setStatus(null);
    try {
      const r = await onSendEmail(email.trim());
      setStatus({ kind: r.ok ? "ok" : "err", msg: r.message });
    } catch (e) {
      setStatus({ kind: "err", msg: e instanceof Error ? e.message : String(e) });
    } finally { setSending(false); }
  }

  async function print() {
    setPrinting(true); setStatus(null);
    try { await onPrint(); setStatus({ kind: "ok", msg: "Tiquete enviado a impresión." }); }
    catch (e) { setStatus({ kind: "err", msg: e instanceof Error ? e.message : String(e) }); }
    finally { setPrinting(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl animate-pop">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-semibold text-grafito-700">Factura guardada</h2>
          <button onClick={onNewSale} aria-label="Cerrar" className="text-xl text-grafito-400 hover:text-grafito-700">×</button>
        </div>

        <div className="flex flex-col items-center py-4">
          <div className="mb-3 inline-flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50 ring-4 ring-emerald-100">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <div className="text-center">
            <div className="text-xs uppercase tracking-wide text-grafito-400">Total</div>
            <div className="text-4xl font-extrabold tabular-nums">{money(data.total)}</div>
            <div className="mt-2 text-xs uppercase tracking-wide text-brand">
              Cambio <span className="ml-1 text-base font-bold tabular-nums">{money(data.change)}</span>
            </div>
          </div>
        </div>

        <div className="mt-4">
          <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-grafito-400">Enviar por correo</label>
          <div className="flex gap-2">
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus
              placeholder="cliente@correo.pa"
              className="flex-1 rounded-lg border px-3 py-2.5 outline-none focus:border-brand" />
            <button onClick={send} disabled={sending || !email.trim()}
              className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50">
              ✉ {sending ? "Enviando…" : "Enviar"}
            </button>
          </div>
        </div>

        {status && (
          <div className={`mt-3 rounded-lg px-3 py-2 text-sm ${status.kind === "ok" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>
            {status.msg}
          </div>
        )}

        <div className="mt-6 flex items-center justify-between gap-3">
          <button onClick={print} disabled={printing}
            className="inline-flex items-center gap-1 rounded-lg border border-grafito-300 bg-white px-4 py-2 text-sm font-semibold text-grafito-700 hover:bg-grafito-50 disabled:opacity-50">
            🖨 {printing ? "Imprimiendo…" : "imprimir"}
          </button>
          <button onClick={onNewSale}
            className="rounded-lg bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700">
            Nueva venta
          </button>
        </div>
      </div>
    </div>
  );
}
